import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Tooltip from '@radix-ui/react-tooltip';
import { cva } from 'class-variance-authority';
import { createPlaceholderUrl } from '../../../../../../utils/PlaceholderCreator';

// CVA: variantes del borde del ícono según el origen de la skill (P5)
const iconBorderVariants = cva(
    'w-12 h-12 rounded-lg flex-shrink-0 overflow-hidden border-2 transition-all duration-200',
    {
        variants: {
            origin: {
                innate:    'border-amber-400 dark:border-amber-500 bg-amber-50 dark:bg-amber-900/20',
                awakening: 'border-violet-400 dark:border-violet-500 bg-violet-50 dark:bg-violet-900/20',
                manual:    'border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-700',
            },
            active: {
                true:  'scale-100 opacity-100',
                false: 'scale-[0.85] opacity-60',
            },
        },
        defaultVariants: {
            origin: 'manual',
            active: true,
        },
    }
);

const SkillsRow_DetailsCell = ({
    skill,
    item,
    getIconUrl,
    allSkills,
    shouldHighlight,
}) => {
    const { t } = useTranslation();

    // Calcular el origen de la skill para el borde (P5)
    const skillOrigin = skill.isAuto
        ? (skill.source === 'Innate' ? 'innate' : 'awakening')
        : 'manual';

    const iconUrl = getIconUrl(skill.icon_id);

    // Skill de base (para el badge "upgrades from")
    const baseSkill = skill.base_skill_id
        ? allSkills.find(s => parseInt(s.Id_skill, 10) === parseInt(skill.base_skill_id, 10))
        : null;

    const showBaseTag = baseSkill && (!baseSkill.color || !baseSkill.color.includes('Purple'));

    // Descripción para el tooltip (P7)
    const description = skill.Description || skill.description || null;

    return (
        <div className={`flex items-center gap-4 ${!shouldHighlight ? 'opacity-40' : 'opacity-100'} transition-opacity duration-200`}>

            {/* Ícono con borde de origen (P5) */}
            <div className={iconBorderVariants({ origin: skillOrigin, active: shouldHighlight })}>
                <img
                    src={iconUrl}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                    onError={(e) => { e.target.src = createPlaceholderUrl(48, 48, '?'); }}
                />
            </div>

            {/* Nombre + tooltip (P7) */}
            <div className="min-w-0">
                {description ? (
                    <Tooltip.Root delayDuration={400}>
                        <Tooltip.Trigger asChild>
                            <div className="font-bold text-gray-800 dark:text-gray-100 text-base leading-tight flex items-center gap-2 cursor-help w-fit">
                                <span className="truncate max-w-[180px]">{skill.Skill_name}</span>

                                {/* Badge "from base skill" */}
                                {showBaseTag && (
                                    <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-md border border-gray-200 dark:border-gray-600 ml-1 flex-shrink-0">
                                        <div className="w-3 h-3 rounded-sm overflow-hidden flex-shrink-0">
                                            <img
                                                src={getIconUrl(baseSkill.icon_id)}
                                                className="w-full h-full object-cover"
                                                alt=""
                                            />
                                        </div>
                                        <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                            {t('SkillsRow.label_from')} {baseSkill.Skill_name}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </Tooltip.Trigger>

                        {/* Tooltip con descripción completa (P7) */}
                        <Tooltip.Portal>
                            <Tooltip.Content
                                side="top"
                                align="start"
                                sideOffset={6}
                                className="
                                    z-50 max-w-xs rounded-xl
                                    bg-gray-900/95 backdrop-blur-sm
                                    border border-gray-700
                                    shadow-2xl shadow-black/40
                                    px-3 py-2.5
                                    data-[state=delayed-open]:animate-fade-in-up
                                "
                            >
                                <p className="prose prose-sm prose-invert text-gray-200 text-xs leading-relaxed">
                                    {description}
                                </p>
                                <Tooltip.Arrow className="fill-gray-900" />
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                ) : (
                    /* Sin descripción: sin cursor-help */
                    <div className="font-bold text-gray-800 dark:text-gray-100 text-base leading-tight flex items-center gap-2">
                        <span className="truncate max-w-[180px]">{skill.Skill_name}</span>
                        {showBaseTag && (
                            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-md border border-gray-200 dark:border-gray-600 ml-1 flex-shrink-0">
                                <div className="w-3 h-3 rounded-sm overflow-hidden flex-shrink-0">
                                    <img
                                        src={getIconUrl(baseSkill.icon_id)}
                                        className="w-full h-full object-cover"
                                        alt=""
                                    />
                                </div>
                                <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                    {t('SkillsRow.label_from')} {baseSkill.Skill_name}
                                </span>
                            </div>
                        )}
                    </div>
                )}

                {/* Nombre JP */}
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate max-w-[200px]">
                    {skill.Skill_name_jp}
                </div>

                {/* Indicador de origen (text label) — sutil */}
                {skillOrigin !== 'manual' && (
                    <div className={`text-[9px] font-bold uppercase tracking-widest mt-0.5 ${
                        skillOrigin === 'innate'
                            ? 'text-amber-500 dark:text-amber-400'
                            : 'text-violet-500 dark:text-violet-400'
                    }`}>
                        {skillOrigin}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SkillsRow_DetailsCell;
